import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Badge,
  Button,
  Divider,
  Group,
  NumberInput,
  SimpleGrid,
  Stack,
  Switch,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AppModal } from '../../shared/components/AppModal';
import { IconAlertCircle, IconWand } from '@tabler/icons-react';
import { ApiError } from '../../shared/api/problem';
import {
  getSuggestedMinMax,
  upsertItemPlanning,
  type ItemPlanningDto,
  type SuggestedMinMaxDto,
} from './api';

interface Props {
  opened: boolean;
  onClose: () => void;
  warehouseId: string;
  row: ItemPlanningDto;
  onSaved: () => void;
}

interface FormState {
  isStocked: boolean;
  minQuantity: number | '';
  maxQuantity: number | '';
  leadTimeDays: number | '';
  orderIncrement: number | '';
  weeksOfHistory: number | '';
  weeksOverMinimum: number | '';
  promptReorder: boolean;
  reorderFlag: boolean;
  countTolerance: number | '';
  isCountControlGroup: boolean;
  countPeriodDays: number | '';
}

const num = (v: number | '') => (v === '' ? null : Number(v));

/**
 * Edits one item's planning in one warehouse.
 *
 * The save is a PUT of every settable field, so the form always submits its whole state —
 * clearing a box really clears it. Cycle-count scheduling (last counted, next due) is shown
 * but not editable: those belong to counting and the scheduler.
 */
export function PlanningFormModal({ opened, onClose, warehouseId, row, onSaved }: Props) {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(fromRow(row));
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<SuggestedMinMaxDto | null>(null);

  useEffect(() => {
    if (!opened) return;
    setErrors({});
    setSubmitError(null);
    setSuggestion(null);
    setForm(fromRow(row));
  }, [opened, row]);

  // A suggestion, never a save: it fills the two boxes and the operator decides.
  const suggest = useMutation({
    mutationFn: () =>
      getSuggestedMinMax(warehouseId, row.itemId, {
        weeksOfHistory: num(form.weeksOfHistory),
        leadTimeDays: num(form.leadTimeDays),
        weeksOverMinimum: num(form.weeksOverMinimum),
      }),
    onSuccess: (s) => {
      setSuggestion(s);
      setForm((f) => ({
        ...f,
        minQuantity: s.suggestedMinQuantity,
        maxQuantity: s.suggestedMaxQuantity,
      }));
      setErrors((e) => ({ ...e, minQuantity: undefined, maxQuantity: undefined }));
    },
    onError: () => setSubmitError(t('planning.errors.suggestFailed')),
  });

  const save = useMutation({
    mutationFn: () =>
      upsertItemPlanning(warehouseId, row.itemId, {
        isStocked: form.isStocked,
        minQuantity: num(form.minQuantity),
        maxQuantity: num(form.maxQuantity),
        leadTimeDays: num(form.leadTimeDays),
        orderIncrement: num(form.orderIncrement),
        weeksOfHistory: num(form.weeksOfHistory),
        weeksOverMinimum: num(form.weeksOverMinimum),
        promptReorder: form.promptReorder,
        reorderFlag: form.reorderFlag,
        countTolerance: num(form.countTolerance),
        isCountControlGroup: form.isCountControlGroup,
        countPeriodDays: num(form.countPeriodDays),
      }),
    onSuccess: () => {
      notifications.show({ message: t('planning.saved'), color: 'green' });
      onSaved();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.problem.type?.includes('min_above_max')) {
        setErrors((e) => ({ ...e, minQuantity: t('planning.errors.minAboveMax') }));
        return;
      }
      setSubmitError(t('planning.errors.saveFailed'));
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    const next: typeof errors = {};

    // Mirrors CK_tbl_ItemWarehouseAssignment_Planning so the operator sees the field at
    // fault instead of a constraint violation.
    const min = num(form.minQuantity);
    const max = num(form.maxQuantity);
    if (min !== null && max !== null && min > max) next.minQuantity = t('planning.errors.minAboveMax');
    if (min !== null && min < 0) next.minQuantity = t('planning.errors.notNegative');
    if (max !== null && max < 0) next.maxQuantity = t('planning.errors.notNegative');
    if (num(form.orderIncrement) !== null && Number(form.orderIncrement) <= 0) {
      next.orderIncrement = t('planning.errors.greaterThanZero');
    }
    if (num(form.leadTimeDays) !== null && Number(form.leadTimeDays) < 0) {
      next.leadTimeDays = t('planning.errors.notNegative');
    }
    if (num(form.weeksOfHistory) !== null && Number(form.weeksOfHistory) <= 0) {
      next.weeksOfHistory = t('planning.errors.greaterThanZero');
    }
    if (num(form.countPeriodDays) !== null && Number(form.countPeriodDays) <= 0) {
      next.countPeriodDays = t('planning.errors.greaterThanZero');
    }

    setErrors(next);
    if (Object.values(next).some(Boolean)) return;
    save.mutate();
  }

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={t('planning.editTitle', { item: row.itemNumber })}
      width="lg"
    >
      <form onSubmit={onSubmit} noValidate>
        <Stack gap="md">
          <Group justify="space-between" wrap="nowrap">
            <Text size="sm" c="dimmed">
              {row.description}
            </Text>
            <Badge variant="light">{row.stockingUomCode}</Badge>
          </Group>

          {submitError && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
              {submitError}
            </Alert>
          )}

          <Switch
            label={t('planning.isStocked')}
            description={t('planning.isStockedHint')}
            checked={form.isStocked}
            onChange={(e) => setForm({ ...form, isStocked: e.currentTarget.checked })}
          />

          <Divider label={t('planning.replenishmentSection')} labelPosition="left" />

          {/* Quantities are in the item's stocking unit — shown in the badge above, because
              a bare number here would be ambiguous for anything not counted in eaches. */}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <NumberInput
              label={t('planning.minQuantity', { uom: row.stockingUomCode })}
              value={form.minQuantity}
              onChange={(v) => setForm({ ...form, minQuantity: v === '' ? '' : Number(v) })}
              error={errors.minQuantity}
              min={0}
              decimalScale={5}
            />
            <NumberInput
              label={t('planning.maxQuantity', { uom: row.stockingUomCode })}
              value={form.maxQuantity}
              onChange={(v) => setForm({ ...form, maxQuantity: v === '' ? '' : Number(v) })}
              error={errors.maxQuantity}
              min={0}
              decimalScale={5}
            />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
            <NumberInput
              label={t('planning.leadTimeDays')}
              value={form.leadTimeDays}
              onChange={(v) => setForm({ ...form, leadTimeDays: v === '' ? '' : Number(v) })}
              error={errors.leadTimeDays}
              min={0}
            />
            <NumberInput
              label={t('planning.weeksOfHistory')}
              description={t('planning.weeksOfHistoryHint')}
              value={form.weeksOfHistory}
              onChange={(v) => setForm({ ...form, weeksOfHistory: v === '' ? '' : Number(v) })}
              error={errors.weeksOfHistory}
              min={1}
            />
            <NumberInput
              label={t('planning.weeksOverMinimum')}
              description={t('planning.weeksOverMinimumHint')}
              value={form.weeksOverMinimum}
              onChange={(v) => setForm({ ...form, weeksOverMinimum: v === '' ? '' : Number(v) })}
              min={0}
            />
          </SimpleGrid>

          <Group justify="space-between" align="flex-end" wrap="nowrap">
            <NumberInput
              label={t('planning.orderIncrement')}
              value={form.orderIncrement}
              onChange={(v) => setForm({ ...form, orderIncrement: v === '' ? '' : Number(v) })}
              error={errors.orderIncrement}
              min={0}
              decimalScale={5}
              style={{ flex: 1 }}
            />
            <Button
              variant="light"
              leftSection={<IconWand size={16} />}
              onClick={() => suggest.mutate()}
              loading={suggest.isPending}
            >
              {t('planning.suggest')}
            </Button>
          </Group>

          {suggestion && (
            <Alert color="blue" variant="light">
              {/* Show the working, not just the answer: a suggestion the operator cannot
                  sanity-check is one they will either accept blindly or ignore. */}
              {t('planning.suggestionExplained', {
                average: suggestion.weeklyAverageIssued.toLocaleString(undefined, { maximumFractionDigits: 2 }),
                uom: row.stockingUomCode,
                weeks: suggestion.weeksOfHistory,
                lead: suggestion.leadTimeDays,
                over: suggestion.weeksOverMinimum,
              })}
            </Alert>
          )}

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Switch
              label={t('planning.promptReorder')}
              checked={form.promptReorder}
              onChange={(e) => setForm({ ...form, promptReorder: e.currentTarget.checked })}
            />
            <Switch
              label={t('planning.reorderFlag')}
              checked={form.reorderFlag}
              onChange={(e) => setForm({ ...form, reorderFlag: e.currentTarget.checked })}
            />
          </SimpleGrid>

          <Divider label={t('planning.cycleCountSection')} labelPosition="left" />

          {/* ABC is calculated from inventory valuation at the warehouse level, so it is
              read-only here. Showing the snapshot date matters: a class with no date has
              never been calculated, which is different from an item that scored low. */}
          <Group gap="xs">
            <Text size="sm" fw={500}>
              {t('planning.abcClass')}:
            </Text>
            {row.currentAbcClass ? (
              <>
                <Badge>{row.currentAbcClass}</Badge>
                {row.abcClassifiedAtUtc && (
                  <Text size="xs" c="dimmed">
                    {t('planning.abcAsOf', {
                      date: new Date(row.abcClassifiedAtUtc).toLocaleDateString(),
                    })}
                  </Text>
                )}
              </>
            ) : (
              <Text size="sm" c="dimmed">
                {t('planning.abcNotCalculated')}
              </Text>
            )}
          </Group>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <NumberInput
              label={t('planning.countTolerance', { uom: row.stockingUomCode })}
              value={form.countTolerance}
              onChange={(v) => setForm({ ...form, countTolerance: v === '' ? '' : Number(v) })}
              min={0}
              decimalScale={5}
            />
            <NumberInput
              label={t('planning.countPeriodDays')}
              description={t('planning.countPeriodDaysHint')}
              value={form.countPeriodDays}
              onChange={(v) => setForm({ ...form, countPeriodDays: v === '' ? '' : Number(v) })}
              error={errors.countPeriodDays}
              min={1}
            />
          </SimpleGrid>

          <Switch
            label={t('planning.isCountControlGroup')}
            description={t('planning.isCountControlGroupHint')}
            checked={form.isCountControlGroup}
            onChange={(e) => setForm({ ...form, isCountControlGroup: e.currentTarget.checked })}
          />

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>
              {t('shell.cancel')}
            </Button>
            <Button type="submit" color="invetrak" loading={save.isPending}>
              {t('shell.saveChanges')}
            </Button>
          </Group>
        </Stack>
      </form>
    </AppModal>
  );
}

function fromRow(p: ItemPlanningDto): FormState {
  return {
    isStocked: p.isStocked,
    minQuantity: p.minQuantity ?? '',
    maxQuantity: p.maxQuantity ?? '',
    leadTimeDays: p.leadTimeDays ?? '',
    orderIncrement: p.orderIncrement ?? '',
    weeksOfHistory: p.weeksOfHistory ?? '',
    weeksOverMinimum: p.weeksOverMinimum ?? '',
    promptReorder: p.promptReorder,
    reorderFlag: p.reorderFlag,
    countTolerance: p.countTolerance ?? '',
    isCountControlGroup: p.isCountControlGroup,
    countPeriodDays: p.countPeriodDays ?? '',
  };
}
